"use client";

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import {
  AlertCircle,
  CheckCircle2,
  Clipboard,
  Gift,
  Loader2,
  Power,
  PowerOff,
  RefreshCw,
  Share2,
  Target,
  UserCheck,
} from "lucide-react";
import { referralServices } from "../../services/referralService";

import "./accountmanage.css";

const REFERRALS_PER_PAGE = 6;
const REFERRALS_FETCH_LIMIT = 1000;

const unwrapResponse = (response) => {
  const body = response?.data;
  if (body?.data !== undefined) return body.data;
  return body;
};

const normalizeReferralCode = (payload) => {
  const data =
    payload?.referral_code && typeof payload.referral_code === "object"
      ? payload.referral_code
      : payload?.code_data || payload;

  if (!data || typeof data === "string") {
    return {
      code: typeof data === "string" ? data : "",
      isActive: Boolean(data),
      url: "",
      createdAt: "",
    };
  }

  return {
    id: data.id,
    code: data.code || data.referral_code || data.referralCode || "",
    isActive:
      data.is_active ??
      data.active ??
      (data.status ? data.status === "active" : true),
    url: data.referral_url || data.share_url || data.url || data.link || "",
    createdAt: data.created_at || data.createdAt || "",
  };
};

const normalizeReferrals = (payload) => {
  const list =
    payload?.referrals || payload?.items || payload?.data || payload || [];
  return Array.isArray(list) ? list : [];
};

const getReferralUser = (referral) =>
  referral.referred_user || referral.referredUser || referral.user || referral;

const getReferralName = (referral) => {
  const user = getReferralUser(referral);
  return (
    user.full_name ||
    user.display_name ||
    user.name ||
    `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
    user.username ||
    "-"
  );
};

const getReferralUserType = (referral) => {
  const user = getReferralUser(referral);
  return user.user_type ? user.user_type.replace(/_/g, " ") : "";
};

const getReferralProfileImage = (referral) => {
  const user = getReferralUser(referral);
  return (
    user.profile_image_url || user.profileImageUrl || user.profile_image || ""
  );
};

const getReferralProfileId = (referral) => {
  const user = getReferralUser(referral);
  return user.id || user.user_id || user.username || "";
};

const getReferralDate = (referral, locale) => {
  const rawDate =
    referral.registered_at ||
    referral.registeredAt ||
    referral.created_at ||
    referral.createdAt ||
    referral.joined_at ||
    referral.referred_at;
  if (!rawDate) return "";
  const parsed = new Date(rawDate);
  if (Number.isNaN(parsed.getTime())) return rawDate;
  return new Intl.DateTimeFormat(locale || undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  }).format(parsed);
};

const ReferralCodeSection = () => {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const isRTL = i18n.dir() === "rtl";

  const [referralCode, setReferralCode] = useState(null);
  const [shareMessage, setShareMessage] = useState("");
  const [referrals, setReferrals] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(null);
  const [notice, setNotice] = useState(null);
  const noticeTimeoutRef = useRef(null);

  const [referralsPage, setReferralsPage] = useState(1);

  const shareLink = useMemo(() => {
    if (referralCode?.url) return referralCode.url;
    if (!referralCode?.code) return "";
    return `${window.location.origin}/register?ref=${referralCode.code}`;
  }, [referralCode]);

  const showNotice = useCallback((type, message) => {
    setNotice({ type, message });
    window.clearTimeout(noticeTimeoutRef.current);
    noticeTimeoutRef.current = window.setTimeout(() => setNotice(null), 3500);
  }, []);

  const loadReferralData = useCallback(async () => {
    try {
      setIsLoading(true);

      const [codeResponse, messageResponse, referralsResponse] =
        await Promise.allSettled([
          referralServices.getMyCode(),
          referralServices.getShareMessage(),
          referralServices.getMyReferrals(1, REFERRALS_FETCH_LIMIT),
        ]);

      if (codeResponse.status === "fulfilled") {
        const nextCode = normalizeReferralCode(
          unwrapResponse(codeResponse.value),
        );

        setReferralCode((currentCode) =>
          nextCode.code || !currentCode?.code ? nextCode : currentCode,
        );
      }

      if (messageResponse.status === "fulfilled") {
        const payload = unwrapResponse(messageResponse.value);

        setShareMessage(
          payload?.message || payload?.share_message || payload || "",
        );
      }

      if (referralsResponse.status === "fulfilled") {
        const unwrapped = unwrapResponse(referralsResponse.value);

        setReferrals(normalizeReferrals(unwrapped));
        setReferralsPage(1);
      }

      if (codeResponse.status === "rejected") {
        setReferralCode((currentCode) =>
          currentCode?.code ? currentCode : null,
        );
      }
    } catch (error) {
      showNotice("error", t("accountPreferences.referral.status.loadError"));
    } finally {
      setIsLoading(false);
    }
  }, [showNotice, t]);

  useEffect(() => {
    loadReferralData();
  }, [loadReferralData]);

  const refreshCodeState = async (response) => {
    const payload = unwrapResponse(response);
    const nextCode = normalizeReferralCode(payload);
    setReferralCode(nextCode);
    await loadReferralData();
  };

  const handleGenerate = async () => {
    try {
      setActionLoading("generate");
      const response = await referralServices.generate();
      await refreshCodeState(response);
      showNotice("success", t("accountPreferences.referral.status.generated"));
    } catch (error) {
      showNotice(
        "error",
        error.response?.data?.message ||
          t("accountPreferences.referral.status.generateError"),
      );
    } finally {
      setActionLoading(null);
    }
  };

  const handleToggleActive = async () => {
    const shouldActivate = !referralCode?.isActive;
    if (!shouldActivate && !referralCode?.code) return;

    try {
      setActionLoading("toggle");
      const response = shouldActivate
        ? await referralServices.activate()
        : await referralServices.deactivate();
      await refreshCodeState(response);
      showNotice(
        "success",
        shouldActivate
          ? t("accountPreferences.referral.status.activated")
          : t("accountPreferences.referral.status.deactivated"),
      );
    } catch (error) {
      showNotice(
        "error",
        error.response?.data?.message ||
          t("accountPreferences.referral.status.toggleError"),
      );
    } finally {
      setActionLoading(null);
    }
  };

  const copyText = async (text) => {
    if (!text) return false;
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch {
      const input = document.createElement("textarea");
      input.value = text;
      input.setAttribute("readonly", "");
      input.style.position = "absolute";
      input.style.left = "-9999px";
      document.body.appendChild(input);
      input.select();
      const copied = document.execCommand("copy");
      document.body.removeChild(input);
      return copied;
    }
  };

  const handleCopyLink = async () => {
    const copied = await copyText(referralCode?.code || "");
    showNotice(
      copied ? "success" : "error",
      copied
        ? t("accountPreferences.referral.status.copied")
        : t("accountPreferences.referral.status.copyError"),
    );
  };

  const handleShare = async () => {
    const text =
      shareMessage ||
      t("accountPreferences.referral.shareFallback", {
        code: referralCode?.code,
        link: shareLink,
      });

    if (navigator.share) {
      try {
        await navigator.share({
          title: t("accountPreferences.referral.shareTitle"),
          text,
          url: shareLink,
        });
        return;
      } catch (error) {
        if (error.name === "AbortError") return;
      }
    }

    const copied = await copyText(text);
    showNotice(
      copied ? "success" : "error",
      copied
        ? t("accountPreferences.referral.status.shareCopied")
        : t("accountPreferences.referral.status.copyError"),
    );
  };

  const referralsTotal = referrals.length;
  const referralsLastPage = Math.max(
    1,
    Math.ceil(referrals.length / REFERRALS_PER_PAGE),
  );
  const visibleReferrals = referrals.slice(
    (referralsPage - 1) * REFERRALS_PER_PAGE,
    referralsPage * REFERRALS_PER_PAGE,
  );
  const canGoPrevious = referralsPage > 1;
  const canGoNext = referralsPage < referralsLastPage;

  const handleReferralsPageChange = (nextPage) => {
    if (nextPage < 1 || nextPage > referralsLastPage) return;

    setReferralsPage(nextPage);
  };

  if (isLoading) {
    return (
      <div className="referralsettings-loading">
        <Loader2 className="referralsettings-spinner" size={34} />
        <p>{t("accountPreferences.referral.loading")}</p>
      </div>
    );
  }

  return (
    <div
      className={`referralsettings-container ${isRTL ? "rtl" : ""}`}
      dir={i18n.dir()}
    >
      {notice && (
        <div
          className={`referralsettings-notice ${notice.type === "error" ? "error" : ""}`}
        >
          {notice.type === "error" ? (
            <AlertCircle size={18} />
          ) : (
            <CheckCircle2 size={18} />
          )}
          <span>{notice.message}</span>
        </div>
      )}

      <section className="referralsettings-hero">
        <div className="referralsettings-hero-copy">
          <span
            className={`referralsettings-status-pill ${referralCode?.isActive ? "active" : "inactive"}`}
          >
            {referralCode?.isActive
              ? t("accountPreferences.referral.active")
              : t("accountPreferences.referral.inactive")}
          </span>
          <h3>
            <Target size={22} />
            {t("accountPreferences.referral.title")}
          </h3>
          <p>{t("accountPreferences.referral.description")}</p>
        </div>

        <div className="referralsettings-code-card">
          <span>{t("accountPreferences.referral.codeLabel")}</span>
          <strong>
            {referralCode?.code && referralCode?.isActive
              ? referralCode.code
              : "-"}
          </strong>
        </div>
      </section>

      <div className="referralsettings-actions-grid">
        <button
          type="button"
          className="referralsettings-secondary-btn"
          onClick={handleCopyLink}
          disabled={!referralCode?.code || !referralCode?.isActive}
        >
          <Clipboard size={18} />
          {t("accountPreferences.referral.actions.copy")}
        </button>

        <button
          type="button"
          className="referralsettings-secondary-btn"
          onClick={handleShare}
          disabled={!referralCode?.code || !referralCode?.isActive}
        >
          <Share2 size={18} />
          {t("accountPreferences.referral.actions.share")}
        </button>

        <button
          type="button"
          className={`referralsettings-danger-btn ${referralCode?.isActive ? "" : "activate"}`}
          onClick={handleToggleActive}
          disabled={actionLoading === "toggle"}
        >
          {actionLoading === "toggle" ? (
            <Loader2 size={18} className="referralsettings-spin-icon" />
          ) : referralCode?.isActive ? (
            <PowerOff size={18} />
          ) : (
            <Power size={18} />
          )}
          {referralCode?.isActive
            ? t("accountPreferences.referral.actions.deactivate")
            : t("accountPreferences.referral.actions.activate")}
        </button>
      </div>

      <section className="referralsettings-section">
        <div className="referralsettings-section-header">
          <div>
            <h3>
              <Gift size={20} />
              {t("accountPreferences.referral.newCode.title")}
            </h3>
            <p>{t("accountPreferences.referral.newCode.description")}</p>
          </div>
          <button
            type="button"
            onClick={handleGenerate}
            disabled={actionLoading === "generate"}
          >
            <RefreshCw
              size={18}
              className={
                actionLoading === "generate" ? "referralsettings-spin-icon" : ""
              }
            />
            {t("accountPreferences.referral.newCode.button")}
          </button>
        </div>
      </section>

      <section className="referralsettings-section">
        <div className="referralsettings-list-header">
          <h3>
            <UserCheck size={20} />
            {t("accountPreferences.referral.referrals.title")}
          </h3>
          <span>
            {t("accountPreferences.referral.referrals.count", {
              count: referralsTotal,
            })}
          </span>
        </div>

        {referrals.length > 0 ? (
          <>
            <div className="referralsettings-table-wrap">
              <table className="referralsettings-table">
                <thead>
                  <tr>
                    <th>{t("accountPreferences.referral.referrals.user")}</th>
                    <th>
                      {t("accountPreferences.referral.referrals.userType")}
                    </th>
                    <th>
                      {t("accountPreferences.referral.referrals.registeredAt")}
                    </th>
                    {/* <th>{t("accountPreferences.referral.referrals.code")}</th> */}
                  </tr>
                </thead>
                <tbody>
                  {visibleReferrals.map((referral, index) => (
                    <tr key={referral.id || index}>
                      <td>
                        <div className="referralsettings-table-user">
                          <div className="referralsettings-referral-avatar">
                            {getReferralProfileImage(referral) ? (
                              <img
                                src={getReferralProfileImage(referral)}
                                alt={getReferralName(referral)}
                                onError={(e) => {
                                  e.currentTarget.style.display = "none";
                                }}
                              />
                            ) : (
                              getReferralName(referral).charAt(0)
                            )}
                          </div>
                          <button
                            type="button"
                            className="referralsettings-referral-name"
                            onClick={() => {
                              const profileId = getReferralProfileId(referral);
                              if (profileId) {
                                navigate(`/social/profile/${profileId}`);
                              }
                            }}
                          >
                            {getReferralName(referral)}
                          </button>
                        </div>
                      </td>
                      <td>
                        <span className="referralsettings-table-pill">
                          {getReferralUserType(referral) || "-"}
                        </span>
                      </td>
                      <td>{getReferralDate(referral, i18n.language) || "-"}</td>
                      {/* <td>{referral.referral_code || "-"}</td> */}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="referralsettings-pagination">
              <button
                type="button"
                onClick={() => handleReferralsPageChange(referralsPage - 1)}
                disabled={!canGoPrevious}
              >
                {t("accountPreferences.referral.referrals.previous")}
              </button>
              <span>
                {t("accountPreferences.referral.referrals.page", {
                  current: referralsPage,
                  total: referralsLastPage,
                })}
              </span>
              <button
                type="button"
                onClick={() => handleReferralsPageChange(referralsPage + 1)}
                disabled={!canGoNext}
              >
                {t("accountPreferences.referral.referrals.next")}
              </button>
            </div>
          </>
        ) : (
          <div className="referralsettings-empty">
            {t("accountPreferences.referral.referrals.empty")}
          </div>
        )}
      </section>
    </div>
  );
};

export default ReferralCodeSection;
